import {RemoteNodeInsightsDataWithSource, UNSEEN_NODES_OUTPUT_ID} from '../campaignAnalytics.types';
import _reduce from 'lodash/reduce';
import _keys from 'lodash/keys';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import {isInsightOfCurrentVersionFallbackCreative} from './isInsightOfCurrentVersionFallbackCreative';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import _pickBy from 'lodash/pickBy';
import {isInsightsOfCurrentVersionConnector} from './isInsightsOfCurrentVersionConnector';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import {DEFAULT_INSIGHTS_DATA} from '../campaignAnalytics.constants';
import _groupBy from 'lodash/groupBy';
import {Nodes} from 'common/src/modules/decisionTree/decisionTree.types';

export function getNodeInsightsByOutputs({
  nodesIdsForCampaignInsights,
  fallbackCreativeNodeId,
  nodes,
  insights,
}: {
  nodes: Nodes;
  nodesIdsForCampaignInsights: number[];
  insights: RemoteNodeInsightsDataWithSource[];
  fallbackCreativeNodeId: number;
}): Record<string, RemoteNodeInsightsDataWithSource[]> {
  const remoteGroupedNodes = _groupBy(insights, 'sourceNodeId');

  return _reduce(
    _keys(nodes),
    (groupedNodes, collectionID) => {
      if (!_includes(nodesIdsForCampaignInsights, Number(collectionID))) {
        return {...groupedNodes};
      }

      let outputs = _filter(nodes[+collectionID].outputs, ({nodeId}) =>
        _includes(nodesIdsForCampaignInsights, nodeId),
      );

      const fallbackCreativeOutput = _find(
        remoteGroupedNodes[collectionID],
        ({targetNodeId, sourceNodeId}) =>
          isInsightOfCurrentVersionFallbackCreative({
            targetNodeId,
            fallbackCreativeNodeId,
            sourceNodeId,
          }),
      );

      if (fallbackCreativeOutput) {
        outputs = [
          ...outputs,
          {
            nodeId: fallbackCreativeNodeId,
            id: _find(nodes[+collectionID].outputs, ({type}) => type === OutputType.Fallback)?.id as number,
          },
        ];
      }

      const groupedNodeContent = _pickBy(remoteGroupedNodes[collectionID], (insightOutput) =>
        isInsightsOfCurrentVersionConnector({
          nodes,
          fallbackCreativeNodeId,
          insightOutput,
          sourceNodeId: +collectionID,
        }),
      );

      let fullNodeData = _map(outputs, ({nodeId, id}) => {
        const remoteData = _find(
          groupedNodeContent,
          (content) => content.targetNodeId === nodeId && content.outputId === id,
        );

        return {
          targetNodeId: nodeId,
          sourceNodeId: +collectionID,
          ...(remoteData ? remoteData : {}),
          outputId: id,
        };
      });

      const unseenNodeOutputs = remoteGroupedNodes[collectionID]?.filter(
        (insightOutput) =>
          !isInsightsOfCurrentVersionConnector({
            nodes,
            fallbackCreativeNodeId,
            insightOutput,
            sourceNodeId: +collectionID,
          }),
      );

      if (!!unseenNodeOutputs?.length) {
        fullNodeData = [
          ...fullNodeData,
          {
            outputId: UNSEEN_NODES_OUTPUT_ID,
            targetNodeId: UNSEEN_NODES_OUTPUT_ID,
            sourceNodeId: +collectionID,
            ..._reduce(
              unseenNodeOutputs,
              (acc, output) =>
                _mapValues(
                  acc,
                  (val: number, propName: keyof RemoteNodeInsightsDataWithSource) =>
                    val + (output[propName] as number),
                ),
              DEFAULT_INSIGHTS_DATA,
            ),
          },
        ];
      }

      return {...groupedNodes, [collectionID]: fullNodeData};
    },
    {},
  );
}
